import React, { useState } from "react";
import { ChevronDown, ChevronRight, AlertCircle, CheckCircle, AlertTriangle } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { HierarchyNode, ValidationItem } from "@/types/dataQuality";

interface ExpandableValidationTableProps {
  hierarchy: HierarchyNode[];
}

const getStatusIcon = (status: "ok" | "error" | "warning") => {
  switch (status) {
    case "ok":
      return <CheckCircle className="h-4 w-4 text-green-600" />;
    case "error":
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    case "warning":
      return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
  }
};

const getStatusBadge = (summary: { errors: number; warnings: number; valids: number }) => {
  if (summary.errors > 0) {
    return <Badge variant="destructive">{summary.errors} erro(s)</Badge>;
  }
  if (summary.warnings > 0) {
    return <Badge className="bg-yellow-600">{summary.warnings} aviso(s)</Badge>;
  }
  return <Badge className="bg-green-600">{summary.valids} válido(s)</Badge>;
};

export function ExpandableValidationTable({ hierarchy }: ExpandableValidationTableProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(
    new Set(["Ferrovia", "Porto", "Terminal"])
  );
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleNode = (nodeId: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  };

  const toggleItem = (itemPath: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemPath)) {
        newSet.delete(itemPath);
      } else {
        newSet.add(itemPath);
      }
      return newSet;
    });
  };

  const renderNode = (node: HierarchyNode, depth: number = 0): React.ReactNode => {
    const nodeId = `${node.level}-${node.name}`;
    const isExpanded = expandedNodes.has(node.name);
    const paddingLeft = depth * 24;

    return (
      <React.Fragment key={nodeId}>
        {/* Node Header Row */}
        <TableRow className="hover:bg-muted/50">
          <TableCell style={{ paddingLeft: `${paddingLeft + 16}px` }}>
            <button
              onClick={() => toggleNode(node.name)}
              className="flex items-center gap-2 text-left w-full"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 flex-shrink-0" />
              ) : (
                <ChevronRight className="h-4 w-4 flex-shrink-0" />
              )}
              <span className="font-medium">{node.name}</span>
            </button>
          </TableCell>
          <TableCell>
            <div className="flex items-center gap-2">
              {getStatusIcon(node.valid ? "ok" : "error")}
              {getStatusBadge(node.summary)}
            </div>
          </TableCell>
          <TableCell>
            <span className="text-muted-foreground text-sm">
              {node.summary.total} item{node.summary.total !== 1 ? "s" : ""}
            </span>
          </TableCell>
        </TableRow>

        {/* Expanded Content */}
        {isExpanded && (
          <>
            {/* Render Children Nodes */}
            {node.children && node.children.map((child) => renderNode(child, depth + 1))}

            {/* Render Items */}
            {node.items && node.items.map((item) => {
              const isItemExpanded = expandedItems.has(item.path);
              const hasErrors = item.errors && item.errors.length > 0;

              return (
                <React.Fragment key={item.path}>
                  <TableRow className="bg-muted/20 hover:bg-muted/40">
                    <TableCell style={{ paddingLeft: `${paddingLeft + 40}px` }}>
                      <div className="flex items-center gap-2">
                        {hasErrors && (
                          <button
                            onClick={() => toggleItem(item.path)}
                            className="flex-shrink-0"
                          >
                            {isItemExpanded ? (
                              <ChevronDown className="h-3 w-3" />
                            ) : (
                              <ChevronRight className="h-3 w-3" />
                            )}
                          </button>
                        )}
                        <span className="text-sm">
                          {item.identificador || item.path}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(item.status)}
                        <span className="text-sm text-muted-foreground">
                          {item.message}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {hasErrors && (
                        <Badge variant="destructive" className="text-xs">
                          {item.errors?.length} erro{item.errors?.length !== 1 ? "s" : ""}
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Error Details */}
                  {isItemExpanded && hasErrors && (
                    <TableRow className="bg-destructive/5">
                      <TableCell colSpan={3} style={{ paddingLeft: `${paddingLeft + 64}px` }}>
                        <div className="space-y-2 py-2">
                          {item.errors?.map((error, idx) => (
                            <div
                              key={idx}
                              className="text-sm p-3 bg-background rounded-md border border-destructive/20"
                            >
                              <div className="font-medium text-destructive">
                                {error.fieldLabel || error.field}
                              </div>
                              <div className="text-muted-foreground mt-1">
                                {error.message}
                              </div>
                              {error.valor !== undefined && error.valor !== null && (
                                <div className="text-xs text-muted-foreground mt-1">
                                  Valor: {JSON.stringify(error.valor)}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm" style={{ maxHeight: "680px" }}>
      <ScrollArea className="w-full" style={{ height: "680px" }}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[50%]">Item</TableHead>
              <TableHead className="w-[35%]">Status</TableHead>
              <TableHead className="w-[15%]">Detalhes</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {hierarchy.map((node) => renderNode(node))}
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
